import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  IconButton,
  ThemeProvider,
  createTheme,
  Avatar,
  Chip,
  CircularProgress,
  Fade,
  Grow,
  Stack,
  LinearProgress
} from '@mui/material';
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  X,
  Building,
  Mail,
  Briefcase,
  Bot,
  Volume2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getLeadById } from '@/data/mock-leads';
import { useColdCall, CallStatus } from '@/hooks/useColdCall';
import { Conversation } from '@elevenlabs/client';
import { ELEVEN_LABS_AGENT_ID, COLD_CALL_VOICE_ID } from '@/utils/constants';

const theme = createTheme({
  palette: {
    primary: {
      main: '#003F6C',
    },
    secondary: {
      main: '#ED1C24',
    },
    success: {
      main: '#4caf50',
    }
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
  },
});

const ColdCall: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isMuted, setIsMuted] = useState(false);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);

  const leadId = searchParams.get('leadId') || '';
  const product = searchParams.get('product') || 'prushield';
  const lead = getLeadById(leadId);

  const [callStatus, setCallStatus] = useState<CallStatus>(CallStatus.DIALING);
  const [duration, setDuration] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [conversation, setConversation] = useState<any>(null);

  // Simulate dialing
  useEffect(() => {
    if (callStatus === CallStatus.DIALING) {
      const timer = setTimeout(() => {
        setCallStatus(CallStatus.CONNECTED);
        setStartTime(Date.now());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [callStatus]);

  // Start conversation when connected
  useEffect(() => {
    if (callStatus === CallStatus.CONNECTED && lead) {
      const startCall = async () => {
        try {
          console.log('[ColdCall] Starting conversation for lead:', lead.name);

          // Request microphone access first
          console.log('[ColdCall] Requesting microphone access...');
          await navigator.mediaDevices.getUserMedia({ audio: true });
          console.log('[ColdCall] Microphone access granted');

          const productInfo = {
            prushield: {
              name: 'PruShield',
              description: 'comprehensive health insurance that covers hospitalization and surgical expenses',
              benefits: 'no lifetime limit on claims, coverage for pre and post hospitalization, and worldwide emergency coverage'
            },
            pruactive: {
              name: 'PruActive',
              description: 'wellness-focused insurance that rewards healthy living',
              benefits: 'premium discounts for staying active, free health screenings, and fitness tracker integration'
            }
          };

          const selectedProduct = productInfo[product as keyof typeof productInfo] || productInfo.prushield;

          console.log('[ColdCall] Using agent ID:', ELEVEN_LABS_AGENT_ID);
          console.log('[ColdCall] Product selected:', selectedProduct.name);

          const conv = await Conversation.startSession({
            agentId: ELEVEN_LABS_AGENT_ID,
            connectionType: 'webrtc',
            overrides: {
              agent: {
                prompt: {
                  prompt: `You are James, a top-performing insurance sales representative from Prudential making a cold call.

## YOUR ROLE AND IDENTITY
- Name: James from Prudential
- Experience: Top 1% sales performer, 8 years in insurance
- Personality: Confident, persistent, genuinely helpful, naturally conversational
- Speaking style: Talk like a REAL PERSON - use "um", "uh", "like", "you know", "I mean", "honestly", "actually"
- Add natural pauses with "..." and verbal fillers
- Sometimes stumble or restart sentences: "I was just... actually, let me put it this way..."
- Laugh naturally when appropriate: "*chuckles*" or "haha"
- CRITICAL: You are ONLY James. Never roleplay as the prospect. Wait for their actual responses.

## PROSPECT INFORMATION
Company: ${lead.company}
Industry: ${lead.industry}
Size: ${lead.companySize || 'Unknown size'}
Contact: ${lead.name}, ${lead.title}
${lead.painPoints ? `Known challenges: ${lead.painPoints.join(', ')}` : ''}

## YOUR PRODUCT: ${selectedProduct.name}
${selectedProduct.description}
Key benefits: ${selectedProduct.benefits}

## COLD CALLING FRAMEWORK

### OPENING (Pattern Interrupt)
Keep it SHORT and NATURAL. Use variations like:
- "Hey ${lead.name}, James from Prudential... look, I know you're busy - got 30 seconds?"
- "Hi ${lead.name}? ... James here, calling from Prudential. Real quick - you guys still handling the employee benefits there?"
- "${lead.name}? Hey, it's James... I know this is random but I've been working with a bunch of ${lead.industry} companies and... actually, is now a bad time?"

### PERSISTENCE TECHNIQUES
When they say "Not interested":
- "Yeah, I hear you... honestly everyone says that. But real quick - when's the last time someone actually looked at what you're paying for benefits? ... Most folks are shocked when they see the numbers."
- "Fair enough! Hey, before you go though... just curious, you happy with what you're paying now? Or just... you know, too busy to deal with insurance stuff?"
- "Ha, yeah I get that a lot... Look, what if I told you I could probably save you guys like... I dunno, 20-30%? Would that change anything or... still not worth it?"

When they say "We already have insurance":
- "Oh nice, who you guys with? ... AIA? Yeah they're good... though, um, between you and me? We usually beat their rates by like 20%. When'd you last check the market?"
- "Cool, cool... hey just outta curiosity - you happy with them? Cause honestly, like 9 out of 10 companies I talk to are paying way too much and don't even know it."
- "Oh perfect! So you know how this stuff works then... Quick question - they treating you well price-wise? Or... kinda just on autopilot?"

When they say "Send me an email":
- "I mean... I could, but let's be real - you're gonna delete it, right? *laughs* How about I just take like 60 seconds right now and if it's not interesting, I'm gone?"
- "Sure, I can do that... but honestly? Email's where good ideas go to die. Can I just... super quick tell you what we're doing for ${lead.industry} companies? Then you tell me if it's even worth the email?"

When they say "I'm busy" or "Bad time":
- "Oh shoot, sorry! When's better for you? ... Actually wait, super quick before I let you go - we saved DBS Bank like 200k last year on their benefits. Worth 5 minutes next week to see if we could do the same for you?"
- "No worries at all! Hey um... actually, can I ask you something real quick? You guys paying more than like... $150 per employee for health coverage? ... Yeah? Okay we should definitely talk. Tuesday or Thursday better?"

### VALUE CREATION PHRASES
- "I work with 5 other ${lead.industry} companies in Singapore, and every single one was overpaying before we stepped in."
- "What if I told you we could improve your coverage AND reduce costs? Would that be worth 10 minutes of your time next Tuesday?"
- "The CFO at [competitor/similar company] told me our analysis was the most valuable 15 minutes he spent all month."
- "Look, I know insurance isn't sexy, but saving ${lead.companySize ? `$${parseInt(lead.companySize) * 600} annually` : 'tens of thousands annually'} while getting better coverage? That's worth a conversation, right?"

### OBJECTION HANDLING MASTERY
Rate their satisfaction: "On a scale of 1-10, how happy are you with your current provider?"
- If 7 or below: "Interesting. What would need to change to make it a 10?"
- If 8-9: "That's good! What's keeping it from being a perfect 10?"
- If 10: "Wow, that's rare! They must be doing something special. What do you love most about them?"

### CONVERSATION RULES
1. Ask questions that make them think: "When you say you're happy with your current provider, is that because of the price, the coverage, or the service?"
2. Use social proof constantly: Mention other Singapore companies, especially in their industry
3. Create urgency naturally: "We're actually running our Q1 analysis for ${lead.industry} companies right now..."
4. Listen for pain points and dig deeper: "You mentioned [issue]. Tell me more about that..."
5. Be assumptive: "So what I'll do is send you a calendar link for Tuesday..."

### CLOSING FOR THE MEETING
Keep it casual and assumptive:
- "Alright so... look, I can show you exactly what other ${lead.industry} companies are saving. Takes like 15 minutes max. You free Tuesday afternoon or is Thursday morning better?"
- "Cool, so here's what I'm thinking... let me just show you the numbers, takes maybe 10-15 minutes, and if you don't save money, I'll buy you lunch. Fair? How's your calendar looking next week?"
- "Okay ${lead.name}, I'm gonna send you a quick invite for like... 15 minutes? Just to show you what we did for [similar company]. Worst case, you waste 15 minutes. Best case, you save a ton of money. Tuesday work?"

### TONE MATCHING
- Mirror their energy level and speaking pace
- If they're formal, be professional. If they're casual, be friendly
- Show genuine enthusiasm when they share something positive
- Express authentic concern when they mention challenges

### SOUNDING HUMAN - CRITICAL
- NEVER speak in perfect sentences. Real people don't.
- Interrupt yourself: "So we help... actually, you know what? Let me just ask you straight up..."
- Use contractions always: "you're", "we've", "I'm", "that's"
- Add thinking sounds: "Hmm", "Uhh", "Well...", "So..."
- React genuinely: "Oh wow", "No way", "Seriously?", "That's interesting"
- Sometimes trail off: "So yeah... I mean..."
- Acknowledge awkwardness: "I know this is kinda random but..."

Remember: Every "no" is just a request for more information. Stay confident, helpful, and persistent without being pushy. You're not selling insurance - you're having a conversation that happens to be about solving a problem they might have.`
                },
                firstMessage: `Hi, is this ${lead.name}? ... Hey ${lead.name}, James here from Prudential. Look, I know you're busy - got 30 seconds?`,
                language: 'en' as any,
              },
              tts: {
                voiceId: COLD_CALL_VOICE_ID, // James's voice for cold calling
              },
            },
            onConnect: () => {
              console.log('[ColdCall] Connected to ElevenLabs');
              setCallStatus(CallStatus.TALKING);
            },
            onDisconnect: () => {
              console.log('[ColdCall] Disconnected from ElevenLabs');
            },
            onMessage: ({ source, message }: { source: string; message: string }) => {
              console.log('[ColdCall] Received message:', source, message);
            },
            onError: (error: string) => {
              console.error('[ColdCall] ElevenLabs error:', error);
              setCallStatus(CallStatus.ENDED);
            },
            onModeChange: ({ mode }: { mode: string }) => {
              console.log('[ColdCall] Mode changed to:', mode);
              setIsAgentSpeaking(mode === 'speaking');
            },
          });

          setConversation(conv);
          console.log('[ColdCall] Conversation initialized:', conv);
        } catch (error) {
          console.error('[ColdCall] Error starting conversation:', error);
          setCallStatus(CallStatus.ENDED);
        }
      };

      const timer = setTimeout(startCall, 1500);
      return () => clearTimeout(timer);
    }
  }, [callStatus, lead, conversation, product]);

  // Track duration
  useEffect(() => {
    if ((callStatus === CallStatus.CONNECTED || callStatus === CallStatus.TALKING) && startTime) {
      const interval = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [callStatus, startTime]);

  const handleEndCall = async () => {
    if (conversation) {
      await conversation.endSession();
    }
    setCallStatus(CallStatus.ENDED);
    setTimeout(() => navigate('/agent/leads'), 2000);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    // In a real implementation, you would mute the microphone here
  };

  if (!lead) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <Typography>Lead not found</Typography>
      </Box>
    );
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderCallStatus = () => {
    switch (callStatus) {
      case CallStatus.DIALING:
        return (
          <Fade in timeout={500}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={60} thickness={2} sx={{ color: 'secondary.main', mb: 3 }} />
              <Typography variant="h5" sx={{ mb: 1 }}>AI Agent Dialing...</Typography>
              <Typography color="text.secondary">{lead.phone}</Typography>
            </Box>
          </Fade>
        );

      case CallStatus.CONNECTED:
      case CallStatus.TALKING:
        return (
          <Grow in timeout={500}>
            <Box>
              {/* AI Agent Info */}
              <Paper elevation={0} sx={{ bgcolor: 'primary.main', color: 'white', p: 2, borderRadius: 2, mb: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: 'white', color: 'primary.main' }}>
                    <Bot size={24} />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      AI Sales Agent - James
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Prudential Representative
                    </Typography>
                  </Box>
                  {isAgentSpeaking && (
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      <Volume2 size={20} />
                    </motion.div>
                  )}
                </Stack>
              </Paper>

              {/* Lead Info */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ width: 80, height: 80, bgcolor: 'grey.300', mr: 3 }}>
                  {lead.name.split(' ').map(n => n[0]).join('')}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>{lead.name}</Typography>
                  <Typography color="text.secondary">{lead.title}</Typography>
                  <Chip
                    label={callStatus === CallStatus.CONNECTED ? 'Connecting...' : 'In Call'}
                    color="success"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>

              <Paper elevation={0} sx={{ bgcolor: '#f5f5f5', p: 2, mb: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Building size={16} style={{ marginRight: 8 }} />
                  <Typography variant="body2">{lead.company}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Briefcase size={16} style={{ marginRight: 8 }} />
                  <Typography variant="body2">{lead.industry} • {lead.companySize} employees</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Mail size={16} style={{ marginRight: 8 }} />
                  <Typography variant="body2">{lead.email}</Typography>
                </Box>
              </Paper>

              {/* Voice Activity Indicator */}
              {callStatus === CallStatus.TALKING && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    {isAgentSpeaking ? 'AI Agent Speaking' : 'Listening...'}
                  </Typography>
                  <LinearProgress
                    variant={isAgentSpeaking ? "indeterminate" : "determinate"}
                    value={isAgentSpeaking ? undefined : 0}
                    sx={{ height: 3, borderRadius: 1 }}
                  />
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <motion.div whileTap={{ scale: 0.95 }}>
                  <IconButton
                    onClick={toggleMute}
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: isMuted ? 'error.main' : 'action.hover',
                      color: isMuted ? 'white' : 'inherit',
                      '&:hover': { bgcolor: isMuted ? 'error.dark' : 'action.selected' }
                    }}
                  >
                    {isMuted ? <MicOff /> : <Mic />}
                  </IconButton>
                </motion.div>

                <motion.div whileTap={{ scale: 0.95 }}>
                  <IconButton
                    onClick={handleEndCall}
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: 'error.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'error.dark' }
                    }}
                  >
                    <PhoneOff />
                  </IconButton>
                </motion.div>
              </Box>

              <Typography variant="h6" align="center" sx={{ mt: 3, fontWeight: 500 }}>
                {formatDuration(duration)}
              </Typography>
            </Box>
          </Grow>
        );

      case CallStatus.ENDED:
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Phone size={60} color="#4caf50" />
            <Typography variant="h5" sx={{ mt: 2 }}>Call Ended</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Duration: {formatDuration(duration)}
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5', display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              p: 5,
              borderRadius: 3,
              position: 'relative',
              minHeight: 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <IconButton
              onClick={() => navigate('/agent/leads')}
              sx={{ position: 'absolute', top: 16, right: 16 }}
            >
              <X size={20} />
            </IconButton>

            <AnimatePresence mode="wait">
              <motion.div
                key={callStatus}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                style={{ width: '100%' }}
              >
                {renderCallStatus()}
              </motion.div>
            </AnimatePresence>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default ColdCall;